// apps/web/src/features/clients/components/ClientLegalEntitiesSection.tsx
//
// Legal entities for one client, given the same weight as Contacts: a manager
// needs the right counterparty as often as the right person to call (founder
// clarification, 2026-09-05). Active entities are listed; archived ones stay
// reachable behind a labelled toggle so their requisites remain readable.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientLegalEntity, LegalEntityRequisites } from '@magimp/shared';
import { Button, SectionCard } from '../../../ui/index.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';
import { LegalEntityCard } from './LegalEntityCard.js';
import { LegalEntityFormModal } from './LegalEntityFormModal.js';
import { RequisitesSetFormModal } from './RequisitesSetFormModal.js';

export interface ClientLegalEntitiesSectionProps {
  clientId: string;
  clientName: string;
  entities: readonly ClientLegalEntity[];
  /** False for read-only actors and for an archived parent client. */
  canManage: boolean;
  /** True while the CLIENT's own lifecycle write is in flight (AC-10). */
  busy?: boolean;
}

type SetTarget = { entity: ClientLegalEntity; set?: LegalEntityRequisites };

const LIST_STYLE = {
  listStyle: 'none' as const,
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 'var(--s-3)',
};

export function ClientLegalEntitiesSection({
  clientId,
  clientName,
  entities,
  canManage,
  busy = false,
}: ClientLegalEntitiesSectionProps) {
  const { t } = useTranslation(['clients', 'common']);
  const actionStyle = useTouchActionStyle();
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ClientLegalEntity | null>(null);
  const [setTarget, setSetTarget] = useState<SetTarget | null>(null);
  const [showArchive, setShowArchive] = useState(false);

  const active = entities.filter((e) => !e.archivedAt);
  const archived = entities.filter((e) => !!e.archivedAt);

  return (
    <SectionCard
      title={t('clients:sections.legalEntities')}
      count={active.length}
      action={
        canManage ? (
          <Button
            icon="plus"
            kind="secondary"
            size="sm"
            style={actionStyle}
            disabled={busy}
            onClick={() => setAddOpen(true)}
          >
            {t('clients:actions.addLegal')}
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)', minWidth: 0 }}>
        {active.length === 0 ? (
          <p style={{ color: 'var(--fg-3)', margin: 0 }}>{t('clients:emptyLegal')}</p>
        ) : (
          <ul style={LIST_STYLE}>
            {active.map((e) => (
              <LegalEntityCard
                key={e.id}
                clientId={clientId}
                entity={e}
                canManage={canManage}
                busy={busy}
                onEditEntity={setEditTarget}
                onAddSet={(entity) => setSetTarget({ entity })}
                onEditSet={(entity, set) => setSetTarget({ entity, set })}
              />
            ))}
          </ul>
        )}

        {archived.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
            <div>
              <Button
                kind="quiet"
                size="sm"
                style={actionStyle}
                aria-expanded={showArchive}
                onClick={() => setShowArchive((s) => !s)}
                data-testid="client-legal-archive-toggle"
              >
                {showArchive
                  ? t('clients:actions.hideArchive')
                  : t('clients:actions.showArchive', { count: archived.length })}
              </Button>
            </div>
            {showArchive && (
              <ul style={LIST_STYLE}>
                {archived.map((e) => (
                  <LegalEntityCard
                    key={e.id}
                    clientId={clientId}
                    entity={e}
                    canManage={canManage}
                    busy={busy}
                    onEditEntity={setEditTarget}
                    onAddSet={(entity) => setSetTarget({ entity })}
                    onEditSet={(entity, set) => setSetTarget({ entity, set })}
                  />
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {addOpen && (
        <LegalEntityFormModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setAddOpen(false)}
        />
      )}
      {editTarget && (
        <LegalEntityFormModal
          clientId={clientId}
          clientName={clientName}
          entity={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
      {setTarget && (
        <RequisitesSetFormModal
          clientId={clientId}
          entity={setTarget.entity}
          {...(setTarget.set ? { set: setTarget.set } : {})}
          onClose={() => setSetTarget(null)}
        />
      )}
    </SectionCard>
  );
}
